import React from "react";
import { Box, Typography } from "@mui/material";
import ConfigurableDonutChart from "../../ConfigurableDonutChart";
import LoadingSpinner from "../../../common/LoadingSpinner";
import EmptyChartState from "../../../common/EmptyChartState";
import type { VisualizationBase } from "@models/VisualizationBase";
import type { DonutChartConfig } from "@store/slices/donut-chart-config-slice";
import { getTimePeriodFooterText } from "@utils/time-period-display";
import {
  getPointInTimeUnitForPeriod,
  getTimePeriodDates,
} from "@utils/time-period-utils";
import { getRollingAverageComparisonFooterText } from "@utils/rolling-average-utils";
import type { TimePeriod } from "@store/slices/crosstab-config-slice";

const DEFAULT_DONUT_TITLE = "Market Share Analysis";

interface PreviewPanelProps {
  isRefetchingData: boolean;
  isAvailableData: boolean;
  seriesName?: string;
  visualizationBase?: VisualizationBase;
  currentConfig?: DonutChartConfig;
  questions?: { name?: string } | null;
  dataAvailabilityDate?: string | null;
  dashboardDateOverride?: { startDate: string; endDate: string };
  weightsApplied?: boolean;
}

const PreviewPanel: React.FC<PreviewPanelProps> = ({
  isRefetchingData,
  isAvailableData,
  seriesName,
  visualizationBase,
  currentConfig,
  questions,
  dataAvailabilityDate,
  dashboardDateOverride,
  weightsApplied = true,
}) => {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        p: 3,
      }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        Preview
      </Typography>
      <Box
        sx={{
          flex: 1,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          minHeight: 400,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isRefetchingData ? (
          <LoadingSpinner message="Loading data..." />
        ) : isAvailableData ? (
          <EmptyChartState message="This question has no response data available for visualization" />
        ) : (
          <ConfigurableDonutChart dashboardDateOverride={dashboardDateOverride} />
        )}
      </Box>

      <Box
        sx={{
          mt: 1,
          p: 1,
          px: 2.5,
          backgroundColor: "#f9fafb",
          borderRadius: 1,
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          {visualizationBase?.enabled && visualizationBase.name && (
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              Base: {visualizationBase.name}
            </Typography>
          )}
          {currentConfig?.showChartN && currentConfig?.chartN != null && (
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              Chart N: {currentConfig.chartN.toLocaleString()}
            </Typography>
          )}
          <TimePeriodFooter
            currentConfig={currentConfig}
            questions={questions}
            dataAvailabilityDate={dataAvailabilityDate}
          />
          <RollingAverageFooter
            currentConfig={currentConfig}
            dataAvailabilityDate={dataAvailabilityDate}
          />
          {currentConfig?.rollingAverage?.enabled &&
            currentConfig?.comparisonRollingLabel && (
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                {currentConfig.comparisonRollingLabel}
              </Typography>
            )}
          {currentConfig?.rollingPeriodsWarningMessage && (
            <Typography variant="caption" color="warning.main" fontWeight={500}>
              {currentConfig.rollingPeriodsWarningMessage}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            Source: {seriesName || "Sample Data"}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          Weight:{" "}
          {currentConfig?.weight === "DISABLED"
            ? "No Weight"
            : weightsApplied
              ? "Default Weight"
              : "No Weight"}
        </Typography>
      </Box>
    </Box>
  );
};

function TimePeriodFooter({
  currentConfig,
  questions,
  dataAvailabilityDate,
}: {
  currentConfig?: DonutChartConfig;
  questions?: { name?: string } | null;
  dataAvailabilityDate?: string | null;
}) {
  const timePeriodSelection = currentConfig?.timePeriodSelection;

  if (!timePeriodSelection || timePeriodSelection.timePeriod === "all_time")
    return null;

  const hasCustomTitle =
    currentConfig?.title !== "" &&
    currentConfig?.title !== DEFAULT_DONUT_TITLE &&
    questions &&
    currentConfig?.title !== questions.name;

  if (!hasCustomTitle) return null;

  const tp = timePeriodSelection.timePeriod as TimePeriod;
  const isNamedPeriod = getPointInTimeUnitForPeriod(tp) !== null;
  const timePeriodText = getTimePeriodFooterText(
    tp,
    isNamedPeriod ? null : timePeriodSelection.startDate,
    isNamedPeriod ? null : timePeriodSelection.endDate,
    dataAvailabilityDate ?? undefined,
  );

  if (!timePeriodText) return null;

  return (
    <Typography variant="caption" color="text.secondary" fontWeight={500}>
      {timePeriodText}
    </Typography>
  );
}

function RollingAverageFooter({
  currentConfig,
  dataAvailabilityDate,
}: {
  currentConfig?: DonutChartConfig;
  dataAvailabilityDate?: string | null;
}) {
  if (
    !currentConfig?.rollingAverage?.enabled ||
    !currentConfig.rollingAverage.windowSize
  ) {
    return null;
  }

  const timePeriod = currentConfig.timePeriodSelection?.timePeriod as
    | TimePeriod
    | undefined;
  if (!timePeriod || timePeriod === "all_time") return null;

  const isNamedPeriod = getPointInTimeUnitForPeriod(timePeriod) !== null;

  let periodStart = currentConfig.timePeriodSelection?.startDate ?? null;
  let periodEnd = currentConfig.timePeriodSelection?.endDate ?? null;
  if (isNamedPeriod) {
    const fresh = getTimePeriodDates(timePeriod, dataAvailabilityDate ?? undefined);
    if (fresh) {
      periodStart = fresh.startDate;
      periodEnd = fresh.endDate;
    }
  }

  const comparisonText = getRollingAverageComparisonFooterText(
    currentConfig.effectiveRollingPeriodCount ?? currentConfig.rollingAverage.windowSize,
    timePeriod,
    periodStart,
    periodEnd,
  );

  if (!comparisonText) return null;

  return (
    <Typography variant="caption" color="text.secondary" fontWeight={500}>
      {comparisonText}
    </Typography>
  );
}

export default PreviewPanel;
